import axios, { type AxiosResponse } from 'axios';
import {
  type SingleApiResponse,
  type ListApiResponse,
  unwrapApiErrorDetails,
  type SuccessApiResponse
} from '@ianesthesie-kit/services';
import {
  AgeBucket,
  MhoSuggestion,
  MhoSuggestionCreateRequest,
  MhoSuggestionEmphasisUpdateRequest,
  MhoSuggestionReorderRequest,
  MhoSuggestionResetRequest,
  RuleGender
} from './mhoSuggestions.model';

const BASE_URL = '/bff/doctor/hospital-config/mho-suggestions';

export type MhoSuggestionCreateResult = {
  data: MhoSuggestion;
  merged: boolean;
};

export class MhoSuggestionsService {
  public static list(): Promise<{ data: MhoSuggestion[] }> {
    return axios
      .get<ListApiResponse<MhoSuggestion>>(BASE_URL)
      .then((resp: AxiosResponse<ListApiResponse<MhoSuggestion>>) => ({
        data: resp.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static create(
    body: MhoSuggestionCreateRequest
  ): Promise<MhoSuggestionCreateResult> {
    return axios
      .post<SingleApiResponse<MhoSuggestion>>(BASE_URL, body)
      .then((resp: AxiosResponse<SingleApiResponse<MhoSuggestion>>) => ({
        data: resp.data.data,
        merged: resp.status === 200
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static deleteForFilter(
    id: number,
    gender: RuleGender,
    ageBucket: AgeBucket
  ): Promise<SuccessApiResponse> {
    return axios
      .delete<SuccessApiResponse>(`${BASE_URL}/${id}`, {
        params: { gender, ageBucket }
      })
      .then((resp) => resp.data)
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static updateEmphasis(
    id: number,
    body: MhoSuggestionEmphasisUpdateRequest
  ): Promise<{ data: MhoSuggestion }> {
    return axios
      .patch<SingleApiResponse<MhoSuggestion>>(`${BASE_URL}/${id}`, body)
      .then((resp: AxiosResponse<SingleApiResponse<MhoSuggestion>>) => ({
        data: resp.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static reorder(
    body: MhoSuggestionReorderRequest
  ): Promise<SuccessApiResponse> {
    return axios
      .put<SuccessApiResponse>(`${BASE_URL}/order`, body)
      .then((resp) => resp.data)
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static reset(
    body: MhoSuggestionResetRequest
  ): Promise<{ data: MhoSuggestion[] }> {
    return axios
      .post<ListApiResponse<MhoSuggestion>>(`${BASE_URL}/reset`, body)
      .then((resp: AxiosResponse<ListApiResponse<MhoSuggestion>>) => ({
        data: resp.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }
}
